import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { getSectionById } from '../data/newCurriculum';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, Lock, CheckCircle2, Star, Play,
  BookOpen, Mic, HelpCircle, Award
} from 'lucide-react';
import axios from 'axios';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LESSON_TYPE_ICONS = {
  flashcard: { icon: BookOpen, color: 'text-blue-500', bg: 'bg-blue-500/20' },
  quiz: { icon: HelpCircle, color: 'text-purple-500', bg: 'bg-purple-500/20' },
  voice: { icon: Mic, color: 'text-green-500', bg: 'bg-green-500/20' },
  roleplay: { icon: Play, color: 'text-amber-500', bg: 'bg-amber-500/20' },
  exam: { icon: Award, color: 'text-red-500', bg: 'bg-red-500/20' },
  video: { icon: Play, color: 'text-pink-500', bg: 'bg-pink-500/20' },
};

export default function DuolingoSection() {
  const { sectionId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);

  const section = getSectionById(sectionId);

  useEffect(() => {
    fetchProgress();
  }, [sectionId]);

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API}/program/progress`, { withCredentials: true });
      setProgress(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!section) {
    return (
      <div className="min-h-screen bg-[#131F24] flex items-center justify-center">
        <div className="text-white text-center">
          <p>Bölüm bulunamadı</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Ana Sayfaya Dön
          </Button>
        </div>
      </div>
    );
  }

  const completedLessons = progress?.unit_progress || {};

  const isLessonCompleted = (unitId, lessonId) => {
    return completedLessons[`${unitId}`]?.includes(lessonId) || false;
  };

  const isUnitLocked = (unitIndex) => {
    if (unitIndex === 0) return false;
    const prevUnit = section.units[unitIndex - 1];
    const prevUnitProgress = completedLessons[`${prevUnit.id}`] || [];
    return prevUnitProgress.length < prevUnit.lessons.length;
  };

  const getUnitProgress = (unit) => {
    const completed = (completedLessons[`${unit.id}`] || []).length;
    return (completed / unit.lessons.length) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#131F24] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#131F24]">
      {/* Header */}
      <div className={cn('bg-gradient-to-br', section.color, 'pb-6')}>
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri</span>
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center text-5xl">
              {section.icon}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{section.title}</h1>
              <p className="text-white/80">{section.subtitle}</p>
              <p className="text-white/60 text-sm mt-1">{section.units.length} ünite</p>
            </div>
          </div>
        </div>
      </div>

      {/* Units */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="space-y-6">
          {section.units.map((unit, unitIndex) => {
            const locked = isUnitLocked(unitIndex);
            const unitProgress = getUnitProgress(unit);
            
            return (
              <div key={unit.id} className="relative">
                {/* Unit Header */}
                <Card className={cn(
                  'border-0 mb-3',
                  locked ? 'bg-gray-800/50' : 'bg-[#1A2C33]'
                )}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        'w-14 h-14 rounded-xl flex items-center justify-center text-2xl',
                        locked ? 'bg-gray-700' : `bg-gradient-to-br ${section.color}`
                      )}>
                        {locked ? <Lock className="w-6 h-6 text-gray-500" /> : unit.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className={cn(
                          'font-bold text-lg',
                          locked ? 'text-gray-500' : 'text-white'
                        )}>
                          Ünite {unit.id}: {unit.title}
                        </h3>
                        <div className="mt-2">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>{unit.lessons.length} ders</span>
                            <span>{Math.round(unitProgress)}%</span>
                          </div>
                          <Progress value={unitProgress} className="h-2 bg-gray-700" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Lessons */}
                {!locked && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 pl-4">
                    {unit.lessons.map((lesson, lessonIndex) => {
                      const completed = isLessonCompleted(unit.id, lesson.id);
                      const typeConfig = LESSON_TYPE_ICONS[lesson.type] || LESSON_TYPE_ICONS.flashcard;
                      const TypeIcon = typeConfig.icon;
                      const isLessonLocked = lessonIndex > 0 && !isLessonCompleted(unit.id, unit.lessons[lessonIndex - 1].id);
                      
                      return (
                        <button
                          key={lesson.id}
                          onClick={() => !isLessonLocked && navigate(`/lesson/${sectionId}/${unit.id}/${lesson.id}`)}
                          disabled={isLessonLocked}
                          className={cn(
                            'relative p-4 rounded-xl transition-all',
                            isLessonLocked ? 'bg-gray-800/30 cursor-not-allowed' :
                            completed ? 'bg-green-500/20 hover:bg-green-500/30' :
                            'bg-[#1A2C33] hover:bg-[#243B44]'
                          )}
                        >
                          {/* Completion Badge */}
                          {completed && (
                            <div className="absolute -top-1 -right-1">
                              <CheckCircle2 className="w-5 h-5 text-green-500 fill-green-500" />
                            </div>
                          )}
                          
                          {/* Icon */}
                          <div className={cn(
                            'w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2',
                            isLessonLocked ? 'bg-gray-700' : typeConfig.bg
                          )}>
                            {isLessonLocked ? (
                              <Lock className="w-5 h-5 text-gray-500" />
                            ) : (
                              <TypeIcon className={cn('w-6 h-6', typeConfig.color)} />
                            )}
                          </div>
                          
                          {/* Title */}
                          <p className={cn(
                            'text-xs font-medium text-center line-clamp-2',
                            isLessonLocked ? 'text-gray-600' :
                            completed ? 'text-green-400' : 'text-gray-300'
                          )}>
                            {lesson.title}
                          </p>
                          
                          {/* Duration */}
                          <p className="text-xs text-gray-500 text-center mt-1">
                            {lesson.duration} dk
                          </p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
